import { useEffect, useRef, useState } from "react";

const MIN_FILTER_QUERY_LENGTH = 3;

/**
 * Custom hook for managing debounced search filter input with keyboard shortcuts.
 *
 * Features:
 * - Debounced input to avoid excessive re-renders during typing
 * - Keyboard shortcut (Cmd/Ctrl+F) to focus the input
 * - Escape key to blur the input
 * - Clear functionality to reset the filter
 * - Focus state tracking
 * - Automatic cleanup of timers on unmount
 *
 * @param debounceMs - Milliseconds to wait before updating filter query (default: 250)
 * @returns Object containing filter query, input ref, handlers, and focus state
 */
export function useSearchFilter({
  initialFilterQuery = "",
  debounceMs = 250,
}: { initialFilterQuery?: string; debounceMs?: number } = {}) {
  const [filterQuery, setFilterQuery] = useState(initialFilterQuery);
  const [isFocused, setIsFocused] = useState(false);
  const filterInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  // Keyboard shortcuts for filter (Cmd/Ctrl+F to focus, Escape to blur)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const input = filterInputRef.current;
      if (!input) return;

      // // Cmd/Ctrl+F to focus
      // if ((e.metaKey || e.ctrlKey) && e.key === "f") {
      //   e.preventDefault();
      //   if (document.activeElement === input) {
      //     input.select();
      //   } else {
      //     input.focus();
      //   }
      // }

      // Escape to blur
      if (e.key === "Escape" && document.activeElement === input) {
        input.blur();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Auto-track focus state via ref
  useEffect(() => {
    const input = filterInputRef.current;
    if (!input) return;

    function handleFocusEvent() {
      setIsFocused(true);
    }

    function handleBlurEvent() {
      setIsFocused(false);
    }

    input.addEventListener("focus", handleFocusEvent);
    input.addEventListener("blur", handleBlurEvent);

    return () => {
      input.removeEventListener("focus", handleFocusEvent);
      input.removeEventListener("blur", handleBlurEvent);
    };
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Handle input change with debouncing
  function handleFilterChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer to update filter query after debounceMs
    debounceTimerRef.current = setTimeout(() => {
      if (value.trim().length >= MIN_FILTER_QUERY_LENGTH) {
        setFilterQuery(value);
      } else {
        setFilterQuery("");
      }
    }, debounceMs);
  }

  // Handle clear button
  function handleClear() {
    if (filterInputRef.current) {
      filterInputRef.current.value = "";
    }
    setFilterQuery("");
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  }

  // Programmatically set filter query (e.g., from predefined keywords)
  function setFilter(value: string) {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setFilterQuery(value);
  }

  return {
    filterQuery,
    filterInputRef,
    handleFilterChange,
    handleClear,
    setFilter,
    isFocused,
  };
}
